import React from 'react';

import PropTypes from 'prop-types';

import { MagnifyingGlassIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

export type SearchInputProps = StylableComponent<HTMLInputElement> & {
  showIcon?: boolean;
  isOpen: boolean;
  disabled?: boolean;
  placeholder?: string;
};

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      style,
      className,
      showIcon = true,
      placeholder: placeholderProp,
      isOpen,
      ...props
    },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) =>
      require(`../../i18n/${locale}.json`),
    );

    const defaultPlaceholder = T.translate('SEARCH')?.toString();

    const placeholder =
      placeholderProp === undefined ? defaultPlaceholder : placeholderProp;

    return (
      <div
        css={[
          {
            display: isOpen ? 'flex' : 'none',
            flex: 'auto',
            alignItems: 'center',
            overflow: 'hidden',
          },
          style,
        ]}
        className={className}
      >
        {showIcon && (
          <MagnifyingGlassIcon
            size={20}
            style={[
              {
                minWidth: 20,
                marginLeft: `-${theme.spacing.XXXS}px`,
                marginRight: theme.spacing.XXXS,
                color: theme.tokens.colors.icon.tertiary.value,
              },
            ]}
          />
        )}
        <input
          ref={ref}
          data-testid={'Dropdown__input'}
          css={[
            {
              flexGrow: 1,
              overflow: 'hidden',
              border: 'none',
              outline: 'none',
              color: 'inherit',
              '::placeholder': {
                color: theme.tokens.colors.text.tertiary.value,
              },
              backgroundColor: 'transparent',
              ...(!isOpen && {
                cursor: 'pointer',
              }),
            },
            theme.typography.labelMedium,
          ]}
          placeholder={placeholder}
          {...props}
        />
      </div>
    );
  },
);

SearchInput.displayName = 'SearchInput';

SearchInput.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Input placeholder text */
  placeholder: PropTypes.string,
  /** Whether to show the search icon */
  showIcon: PropTypes.bool,
  /** Whether Dropdown menu is open */
  isOpen: PropTypes.bool.isRequired,
};

export default SearchInput;
